import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  AuthUser,
  EmploymentType,
  Industry,
  LocationType,
  Position,
  Seniority,
} from "@/types";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  isSuperAdmin,
  INDUSTRY_OPTIONS,
  LOCATION_OPTIONS,
  SENIORITY_OPTIONS,
} from "@/types";
import { api } from "@/lib/api";

interface Props {
  positions: Position[];
  currentUser?: AuthUser;
  onChange: () => void;
}

export function PositionManager({ positions, currentUser, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterSeniority, setFilterSeniority] = useState<string>("all");
  const [filterIndustry, setFilterIndustry] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterLocation, setFilterLocation] = useState<string>("all");
  const [filterOrgId, setFilterOrgId] = useState<number | null>(null);

  const { data: orgsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin(currentUser),
  });
  const orgs = orgsData?.organizations ?? [];

  const filtered = useMemo(() => {
    let list = positions;
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.description ?? "").toLowerCase().includes(q)
      );
    }
    if (filterSeniority !== "all") {
      list = list.filter((p) => p.seniority === filterSeniority);
    }
    if (filterIndustry !== "all") {
      list = list.filter((p) => p.industry === filterIndustry);
    }
    if (filterType !== "all") {
      list = list.filter((p) => p.employment_type === filterType);
    }
    if (filterLocation !== "all") {
      list = list.filter((p) => p.location === filterLocation);
    }
    if (filterOrgId !== null) {
      list = list.filter((p) => p.org_id === filterOrgId);
    }
    return list;
  }, [positions, search, filterSeniority, filterIndustry, filterType, filterLocation, filterOrgId]);

  const counts = useMemo(() => {
    const s: Record<string, number> = {};
    const i: Record<string, number> = {};
    const t: Record<string, number> = {};
    const l: Record<string, number> = {};
    for (const p of positions) {
      if (p.seniority) s[p.seniority] = (s[p.seniority] || 0) + 1;
      if (p.industry) i[p.industry] = (i[p.industry] || 0) + 1;
      if (p.employment_type) t[p.employment_type] = (t[p.employment_type] || 0) + 1;
      if (p.location) l[p.location] = (l[p.location] || 0) + 1;
    }
    return { seniority: s, industry: i, type: t, location: l };
  }, [positions]);

  const activeFilterCount =
    (filterSeniority !== "all" ? 1 : 0) +
    (filterIndustry !== "all" ? 1 : 0) +
    (filterType !== "all" ? 1 : 0) +
    (filterLocation !== "all" ? 1 : 0) +
    (filterOrgId !== null ? 1 : 0);

  const hasActiveFilters = search !== "" || activeFilterCount > 0;

  const clearFilters = () => {
    setSearch("");
    setFilterSeniority("all");
    setFilterIndustry("all");
    setFilterType("all");
    setFilterLocation("all");
    setFilterOrgId(null);
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this position, all its matrices, and all its candidates?"))
      return;
    await api.deletePosition(id);
    onChange();
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
      {/* Header + search + filter toggle + add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
            Positions
          </h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {positions.length}
          </span>
        </div>
        <div className="flex flex-1 items-center justify-end gap-2 sm:max-w-lg">
          <div className="relative flex-1 sm:max-w-xs">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
            <input
              className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={[
              "flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors",
              filtersOpen || activeFilterCount > 0
                ? "bg-near-black text-white"
                : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
            ].join(" ")}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            className="shrink-0 rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
            onClick={() => setOpen(true)}
          >
            + Add
          </button>
        </div>
      </div>
        </div>
      </div>

      {/* Sticky filter panel */}
      {filtersOpen && (
        <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
          <div className="mx-auto max-w-6xl">
        <div className="rounded-card bg-white p-5 shadow-card">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {isSuperAdmin(currentUser) && orgs.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Organization
                </label>
                <select
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                  value={filterOrgId ?? ""}
                  onChange={(e) => setFilterOrgId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All organizations</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}
            <FilterSelect
              label="Seniority"
              value={filterSeniority}
              onChange={setFilterSeniority}
              options={SENIORITY_OPTIONS}
              counts={counts.seniority}
            />
            <FilterSelect
              label="Industry"
              value={filterIndustry}
              onChange={setFilterIndustry}
              options={INDUSTRY_OPTIONS}
              counts={counts.industry}
            />
            <FilterSelect
              label="Type"
              value={filterType}
              onChange={setFilterType}
              options={EMPLOYMENT_TYPE_OPTIONS}
              counts={counts.type}
            />
            <FilterSelect
              label="Location"
              value={filterLocation}
              onChange={setFilterLocation}
              options={LOCATION_OPTIONS}
              counts={counts.location}
            />
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-text-secondary">
                Showing {filtered.length} of {positions.length} positions
              </p>
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-rausch hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
          </div>
        </div>
      )}

      {/* Scrollable positions list */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
      {filtered.length === 0 ? (
        <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
          {positions.length === 0
            ? 'No positions yet. Click "+ Add position" to get started.'
            : "No positions match the current filters."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="rounded-card bg-white p-5 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-[20px] font-semibold leading-tight tracking-[-0.18px] text-near-black">
                    {p.title}
                  </h3>
                  {isSuperAdmin(currentUser) && p.org_name && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-btn bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                      <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                      </svg>
                      {p.org_name}
                    </span>
                  )}
                  {p.description && (
                    <p className="mt-1 text-sm text-text-secondary">
                      {p.description}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.seniority && <Tag>{p.seniority}</Tag>}
                    {p.industry && <Tag>{p.industry}</Tag>}
                    {p.employment_type && <Tag>{p.employment_type}</Tag>}
                    {p.location && <Tag>{p.location}</Tag>}
                  </div>
                  <p className="mt-2 text-xs text-text-secondary">
                    Created{" "}
                    {new Date(p.created_at + "Z").toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  className="shrink-0 text-xs text-text-secondary transition-colors hover:text-error-text"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
        </div>
      </div>

      {/* Dialog */}
      {open && (
        <AddPositionDialog
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            onChange();
          }}
        />
      )}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-btn bg-surface-secondary px-2.5 py-0.5 text-xs font-medium text-text-focused">
      {children}
    </span>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  counts,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  counts: Record<string, number>;
}) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
        {label}
      </label>
      <select
        className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt} {counts[opt] ? `(${counts[opt]})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function AddPositionDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [seniority, setSeniority] = useState<Seniority | "">("");
  const [industry, setIndustry] = useState<Industry | "">("");
  const [employmentType, setEmploymentType] = useState<EmploymentType | "">("");
  const [location, setLocation] = useState<LocationType | "">("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const submit = async () => {
    setError(null);
    if (!title.trim()) return setError("Title is required");

    setSaving(true);
    try {
      await api.createPosition({
        title: title.trim(),
        description: description.trim() || undefined,
        seniority: seniority || undefined,
        industry: industry || undefined,
        employment_type: employmentType || undefined,
        location: location || undefined,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  const selectClass =
    "mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus";

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div className="mx-4 w-full max-w-lg rounded-card bg-white p-6 shadow-card">
        <div className="flex items-center justify-between">
          <h2 className="text-[22px] font-semibold leading-tight tracking-[-0.44px] text-near-black">
            Add a position
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
          >
            ✕
          </button>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Create a position to start attaching hiring matrices and screening
          candidates.
        </p>

        <div className="mt-5 space-y-4">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-text-secondary">
              Title
            </label>
            <input
              ref={inputRef}
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Senior Backend Engineer"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-text-secondary">
              Description (optional)
            </label>
            <textarea
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief summary of the role"
            />
          </div>

          {/* Metadata — 2x2 grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-text-secondary">
                Seniority
              </label>
              <select
                className={selectClass}
                value={seniority}
                onChange={(e) => setSeniority(e.target.value as Seniority | "")}
              >
                <option value="">— select —</option>
                {SENIORITY_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">
                Industry
              </label>
              <select
                className={selectClass}
                value={industry}
                onChange={(e) => setIndustry(e.target.value as Industry | "")}
              >
                <option value="">— select —</option>
                {INDUSTRY_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">
                Employment type
              </label>
              <select
                className={selectClass}
                value={employmentType}
                onChange={(e) =>
                  setEmploymentType(e.target.value as EmploymentType | "")
                }
              >
                <option value="">— select —</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">
                Location
              </label>
              <select
                className={selectClass}
                value={location}
                onChange={(e) =>
                  setLocation(e.target.value as LocationType | "")
                }
              >
                <option value="">— select —</option>
                {LOCATION_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && <p className="text-sm font-medium text-error-text">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-btn bg-surface-secondary px-5 py-2 text-sm font-medium text-text-focused transition-colors hover:bg-border-card"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              className="rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:bg-border-card disabled:text-text-secondary"
              onClick={submit}
            >
              {saving ? "Saving…" : "Create position"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
